'use client';

import { useTranslation } from 'next-i18next';
import { usePathname } from 'next/navigation';

export interface CurrentMission {
  key: string;
  label: string;
}

const stripLocale = (pathname: string, locale: Locale): string => {
  const prefix = `/${locale}`;
  if (pathname === prefix) return '/';
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  return pathname;
};

/**
 * Maps the current URL to the active mission (for the chip + the "selected" state in the
 * menu). Purely path-based — NOT derived from the prompt-bar mode, because the company
 * context forces chat mode yet a company can still open the candidate job search at
 * `/mc/search`, which must resolve to the `jobs` mission, not `coaching`.
 */
export const useCurrentMission = (locale: Locale): CurrentMission => {
  const { t } = useTranslation('common');
  const pathname = usePathname() ?? '/';
  const path = stripLocale(pathname.split('?')[0], locale).replace(/\/$/, '') || '/';

  if (path === '/cv/search' || path.startsWith('/cv/search/')) {
    return { key: 'talents', label: t('header.label.cv_search') };
  }
  // Employer views of a job's applicants (list, pipeline, input-flow, CV detail) belong to the
  // MyJobs+ mission. Only these subpaths — candidate-facing /job/[slug]/... pages
  // (after-apply, ai-interview, …) must not light MyJobs+.
  if (/^\/job\/[^/]+\/(applicants|pipeline|cv)(\/|$)/.test(path)) {
    return { key: 'candidate-management', label: t('mc.candidate_management') };
  }
  if (
    path === '/profile' ||
    path.startsWith('/profile/') ||
    // Company profile editing is part of the unified profile editor.
    path === '/company/edit' ||
    path.startsWith('/company/edit/')
  ) {
    return { key: 'edit-profile', label: t('header.label.edit_profile') };
  }
  if (path === '/stories' || path.startsWith('/stories/')) {
    return { key: 'interviews', label: t('mc.label.interviews') };
  }
  if (path === '/job-add/edit' || path.startsWith('/job-add')) {
    return { key: 'add-job', label: t('homepage.react.button.addjob') };
  }
  if (
    path === '/my-jobs' || path.startsWith('/my-jobs')
    // Candidate equivalent of "My Jobs" (companies → /my-jobs, candidates → /applied-jobs).
    || path === '/applied-jobs' || path.startsWith('/applied-jobs')
  ) {
    return { key: 'candidate-management', label: t('mc.candidate_management') };
  }
  if (path === '/bestie-chat' || path.startsWith('/bestie-chat/')) {
    return { key: 'coaching', label: t('mc.career.coaching') };
  }

  // Pages that belong to no mission. Without these they fall through to the `jobs`
  // fallback below and light Jobs in the main menu, which is wrong: nothing in the nav
  // corresponds to where the user is. `none` matches no nav item, so every item renders
  // unselected.
  //
  // Neither path is translated (they are absent from `paths.ts`), so the literal prefixes
  // hold in both locales — unlike the job routes, which are.
  if (
    // services, transactions, invoices, billing data
    path === '/wallet' || path.startsWith('/wallet/')
    || path === '/salaries' || path.startsWith('/salaries/')
  ) {
    return { key: 'none', label: '' };
  }

  // /mc, /mc/search, candidate job lists, and any other MC path → job search.
  return { key: 'jobs', label: t('mc.search.job') };
};
